import {
  Button,
  ButtonProps,
  HelpIcon,
  Spinner,
  Tag,
  VariableToken,
} from '@dixa/lib-shared-habla';
import classnames from 'classnames';
import isEqual from 'lodash/isEqual';
import truncate from 'lodash/truncate';
import { PureComponent } from 'react';
import * as React from 'react';

import { RSFieldText } from './rSFieldText';
import styles from './requestSimulator.module.scss';
import { RequestSimulatorFold } from './requestSimulatorFold';
import { RequestSimulatorObjectLine } from './requestSimulatorObjectLine';

export type StatusT = {
  name: string;
  text: string;
  color: 'DEFAULT' | 'RED' | 'GREEN';
};

type VariableItemT = {
  key: string;
  label: string;
};

export type RequestSimulatorPropsT = {
  /** Title */
  title: string;
  /** Title helptext */
  helpText: string;
  /** Extra classnames */
  className?: string;
  /** Data/object/JSON to render */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data?: any;
  /** Variables */
  variables: Array<VariableItemT>;
  /** Array of statuses */
  status: Array<StatusT>;
  /** Display loading indicator */
  loading: boolean;
  /** Show revamped style */
  isAligned?: boolean;
  /** OnChange for variable inputs */
  onChange: (
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
    value: string,
  ) => void;
  /** OnSend */
  onSend: (arg0: {}) => void;
};

type StateT = {
  variableKeys: Array<string>;
  variableValues: { [K in string]: string };
};

export class RequestSimulator extends PureComponent<
  RequestSimulatorPropsT,
  StateT
> {
  static renderStatus({ name, text, color }: StatusT) {
    const colorClass = {
      [styles.statusValue]: true,
      [styles.statusValueGreen]: color === 'GREEN',
      [styles.statusValueRed]: color === 'RED',
    };

    return (
      <div key={name} className={styles.status}>
        {`${name}: `}
        <span className={classnames(colorClass)}>{text}</span>
      </div>
    );
  }

  static getDerivedStateFromProps(
    props: RequestSimulatorPropsT,
    state: StateT,
  ) {
    const { variables = [] } = props;
    const variableKeys: Array<string> = variables.map(
      (variable: VariableItemT) => variable.key,
    );

    if (
      state.variableKeys &&
      variableKeys.length === state.variableKeys.length &&
      isEqual(variableKeys, state.variableKeys)
    ) {
      return null;
    }

    const variableValues = variableKeys.reduce((acc, key) => {
      if (state.variableValues && state.variableValues[key]) {
        acc[key] = state.variableValues[key];
      } else {
        acc[key] = '';
      }

      return acc;
    }, {});

    return {
      variableKeys,
      variableValues,
    };
  }

  constructor(props: RequestSimulatorPropsT) {
    super(props);
    this.state = {
      variableKeys: [],
      variableValues: {},
    };

    this.onVariableChange = this.onVariableChange.bind(this);
    this.onSend = this.onSend.bind(this);
  }

  renderTree(
    data: any,
    dataKey = '',
    nestingLevel = 0,
    path = '',
    isLastLine = false,
  ): string | React.ReactElement<RequestSimulatorFold> {
    if (data === null) {
      return 'null';
    }

    if (typeof data === 'object') {
      const keys = Object.keys(data as object);
      const isArray = Array.isArray(data);

      return (
        <RequestSimulatorFold
          foldKey={dataKey}
          isArray={isArray}
          folded={nestingLevel > 1}
          isLastLine={isLastLine}
        >
          {keys.map((key, index) => {
            let itemPath = path;
            if (path) {
              if (isArray) {
                itemPath += `[${key}]`;
              } else {
                itemPath += `.${key}`;
              }
            } else {
              itemPath = key;
            }

            return (
              <RequestSimulatorObjectLine
                key={key}
                isArray={isArray}
                objKey={key}
                path={itemPath}
                objValue={this.renderTree(
                  data[key],
                  key,
                  nestingLevel + 1,
                  itemPath,
                  index + 1 === keys.length,
                )}
                isLastLine={index + 1 === keys.length}
              />
            );
          })}
        </RequestSimulatorFold>
      );
    }

    if (typeof data === 'boolean') {
      return data ? 'true' : 'false';
    }

    if (typeof data === 'string') {
      if (data.length > 30) {
        return `"${truncate(data, {
          length: 80,
          omission: '[…]',
          separator: /,?\.? +/,
        })}"`;
      }

      return `"${data}"`;
    }

    return data.toString();
  }

  onVariableChange(
    e: React.ChangeEvent<HTMLInputElement>,
    key: string,
    value: string,
  ) {
    const { variableValues } = this.state;
    const { onChange } = this.props;
    if (variableValues[key] !== value) {
      this.setState({
        variableValues: {
          ...variableValues,
          [key]: value,
        },
      });
      onChange(e, key, value);
    }
  }

  onSend() {
    const { onSend } = this.props;
    const { variableValues } = this.state;
    onSend(variableValues);
  }

  render() {
    const {
      className,
      data,
      status,
      variables,
      loading,
      title,
      helpText,
      isAligned,
      onChange,
      onSend,
      ...otherProps
    } = this.props;
    const { variableValues } = this.state;
    const hasVariables = !!(variables && variables.length);
    const hasData = data !== undefined && data !== null;
    const hasStatus = status !== undefined && status !== null;
    const hasDataBlock = hasVariables || hasData || loading;

    const buttonProps: ButtonProps = {
      onClick: this.onSend,
      variant: isAligned ? 'primary' : 'secondary',
    };

    const rootClass = classnames(
      {
        [styles.root]: true,
        [styles.rootHasVariables]: hasVariables,
        [styles.rootIsLoading]: loading,
      },
      className,
    );

    return (
      <div {...otherProps} className={rootClass}>
        <div
          className={classnames(styles.topbar, {
            [styles.topbarAligned]: isAligned,
            [styles.topbarWithDataBlock]: isAligned && hasDataBlock,
          })}
        >
          {!!title && (
            <div className={styles.title}>
              {title}
              {!!helpText && <HelpIcon text={helpText} />}
            </div>
          )}
          <Button {...buttonProps}>Test request</Button>
        </div>

        {hasDataBlock && (
          <div className={styles.requestDataBlock}>
            {hasVariables && (
              <div className={styles.variableContainer}>
                <div className={styles.variables}>
                  <div
                    className={classnames(styles.variableNames, {
                      [styles.variableNamesAligned]: isAligned,
                    })}
                  >
                    {variables.map(({ key, label }) =>
                      isAligned ? (
                        <Tag key={key} className={styles.tag}>
                          {label}
                        </Tag>
                      ) : (
                        <div key={key} className={styles.variableName}>
                          <VariableToken text={label} />
                        </div>
                      ),
                    )}
                  </div>
                  <div className={styles.variableInputs}>
                    {variables.map(({ key }) => (
                      <div key={key} className={styles.variableInput}>
                        &nbsp;
                        {'='}
                        &nbsp;
                        <RSFieldText
                          key={key}
                          name={key}
                          value={variableValues[key] ?? ''}
                          onChange={this.onVariableChange}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
            {(hasData || loading) && (
              <div className={styles.treeContainer}>
                {hasData && this.renderTree(data)}
                {loading && <Spinner />}
              </div>
            )}
            {hasStatus && (
              <div className={styles.statusContainer}>
                {status.map(RequestSimulator.renderStatus)}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }
}
